import React, { useEffect } from 'react';
import {
    Box,
    Button,
    Card,
    Chip,
    Container,
    Stack,
    Typography,
    useTheme,
} from "@mui/material";
import {
    AccountBalanceWalletRounded,
    ArrowForwardRounded,
    CardGiftcardRounded,
    ExploreOffRounded,
    GroupRounded,
    HomeRounded,
    SupportAgentRounded,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";

export function NotFound() {
    const theme = useTheme();
    const navigate = useNavigate();

    useEffect(() => {
        document.title = 'صفحه مورد نظر یافت نشد (۴۰۴) | جیم‌پین سازمانی';
    }, []);

    const quickLinks = [
        { label: "پیشخوان", path: "/dashboard", icon: <HomeRounded sx={{ fontSize: 18 }} /> },
        { label: "پرسنل", path: "/personnel", icon: <GroupRounded sx={{ fontSize: 18 }} /> },
        { label: "تخصیص اعتبار", path: "/allocations", icon: <CardGiftcardRounded sx={{ fontSize: 18 }} /> },
        { label: "امور مالی", path: "/charge", icon: <AccountBalanceWalletRounded sx={{ fontSize: 18 }} /> },
        { label: "پشتیبانی", path: "/support", icon: <SupportAgentRounded sx={{ fontSize: 18 }} /> },
    ];

    return (
        <Container
            maxWidth="md"
            sx={{
                minHeight: '78vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                py: { xs: 4, sm: 6 },
            }}
        >
            <Card
                elevation={0}
                sx={{
                    width: '100%',
                    p: { xs: 3.5, sm: 6 },
                    borderRadius: { xs: 4, sm: 6 },
                    textAlign: 'center',
                    position: 'relative',
                    overflow: 'hidden',
                    border: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'background.paper',
                    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.08)',
                }}
            >
                {/* Ambient Decorative Glow Behind 404 */}
                <Box
                    sx={{
                        position: 'absolute',
                        top: '15%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: { xs: 240, sm: 380 },
                        height: { xs: 160, sm: 220 },
                        borderRadius: '50%',
                        background: theme.palette.mode === 'dark'
                            ? 'radial-gradient(circle, rgba(45, 107, 226, 0.25) 0%, transparent 70%)'
                            : 'radial-gradient(circle, rgba(45, 107, 226, 0.15) 0%, transparent 70%)',
                        filter: 'blur(40px)',
                        pointerEvents: 'none',
                    }}
                />

                {/* Floating Badge / Icon */}
                <Box
                    sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: { xs: 64, sm: 80 },
                        height: { xs: 64, sm: 80 },
                        borderRadius: { xs: 3, sm: 4 },
                        bgcolor: theme.palette.mode === 'dark'
                            ? 'rgba(45, 107, 226, 0.15)'
                            : 'rgba(45, 107, 226, 0.08)',
                        color: 'primary.main',
                        mb: 2,
                        border: '1px solid',
                        borderColor: theme.palette.mode === 'dark'
                            ? 'rgba(45, 107, 226, 0.3)'
                            : 'rgba(45, 107, 226, 0.2)',
                        boxShadow: '0 12px 28px rgba(45, 107, 226, 0.2)',
                        animation: 'floatingIcon 3s ease-in-out infinite',
                        '@keyframes floatingIcon': {
                            '0%, 100%': { transform: 'translateY(0)' },
                            '50%': { transform: 'translateY(-6px)' },
                        },
                    }}
                >
                    <ExploreOffRounded sx={{ fontSize: { xs: 32, sm: 42 } }} />
                </Box>

                {/* Giant Stylized 404 Typography */}
                <Typography
                    variant="h1"
                    sx={{
                        fontWeight: 900,
                        fontSize: { xs: '5.5rem', sm: '8rem', md: '9.5rem' },
                        lineHeight: 1,
                        letterSpacing: '-0.04em',
                        userSelect: 'none',
                        background: theme.palette.mode === 'dark'
                            ? 'linear-gradient(135deg, #60a5fa 0%, #38bdf8 50%, #a855f7 100%)'
                            : 'linear-gradient(135deg, #1c8b9c 0%, #2563eb 50%, #6366f1 100%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        mb: 1.5,
                        textShadow: '0 8px 30px rgba(45, 107, 226, 0.15)',
                    }}
                >
                    ۴۰۴
                </Typography>

                {/* Main Heading */}
                <Typography
                    variant="h4"
                    sx={{
                        fontWeight: 900,
                        fontSize: { xs: '1.4rem', sm: '1.85rem' },
                        color: 'text.primary',
                        mb: 1,
                        letterSpacing: -0.5,
                    }}
                >
                    صفحه مورد نظر پیدا نشد!
                </Typography>

                {/* Descriptive Subtitle */}
                <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{
                        maxWidth: 480,
                        mx: 'auto',
                        mb: 4,
                        fontSize: { xs: '0.9rem', sm: '1rem' },
                        lineHeight: 1.7,
                    }}
                >
                    آدرسی که وارد کرده‌اید وجود ندارد، تغییر مسیر داده شده یا دسترسی به آن امکان‌پذیر نمی‌باشد.
                </Typography>

                {/* Action Buttons */}
                <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={2}
                    justifyContent="center"
                    alignItems="center"
                    sx={{ mb: 4.5 }}
                >
                    <Button
                        variant="contained"
                        size="large"
                        startIcon={<HomeRounded sx={{ ml: 0.5, fontSize: 20 }} />}
                        onClick={() => navigate('/dashboard')}
                        sx={{
                            borderRadius: 3,
                            px: 3.5,
                            py: 1.3,
                            fontWeight: 800,
                            fontSize: '0.95rem',
                            boxShadow: '0 6px 20px rgba(45, 107, 226, 0.35)',
                            minWidth: { xs: '100%', sm: 190 },
                        }}
                    >
                        بازگشت به پیشخوان
                    </Button>

                    <Button
                        variant="outlined"
                        size="large"
                        startIcon={<ArrowForwardRounded sx={{ ml: 0.5, fontSize: 18 }} />}
                        onClick={() => navigate(-1)}
                        sx={{
                            borderRadius: 3,
                            px: 3,
                            py: 1.3,
                            fontWeight: 700,
                            fontSize: '0.95rem',
                            color: 'text.primary',
                            borderColor: theme.palette.mode === 'dark'
                                ? 'rgba(255, 255, 255, 0.15)'
                                : 'rgba(0, 0, 0, 0.15)',
                            minWidth: { xs: '100%', sm: 150 },
                            '&:hover': {
                                borderColor: 'primary.main',
                                bgcolor: 'action.hover',
                            },
                        }}
                    >
                        صفحه قبل
                    </Button>
                </Stack>

                {/* Quick Navigation Links */}
                <Box sx={{ pt: 3, borderTop: '1px dashed', borderColor: 'divider' }}>
                    <Typography
                        variant="caption"
                        sx={{
                            color: 'text.secondary',
                            fontWeight: 700,
                            display: 'block',
                            mb: 1.5,
                        }}
                    >
                        دسترسی سریع به بخش‌های اصلی:
                    </Typography>

                    <Stack
                        direction="row"
                        spacing={1}
                        flexWrap="wrap"
                        useFlexGap
                        justifyContent="center"
                    >
                        {quickLinks.map((item) => (
                            <Chip
                                key={item.path}
                                icon={item.icon}
                                label={item.label}
                                clickable
                                onClick={() => navigate(item.path)}
                                variant="outlined"
                                sx={{
                                    borderRadius: 2.5,
                                    py: 1.8,
                                    px: 0.5,
                                    fontWeight: 700,
                                    fontSize: '0.82rem',
                                    bgcolor: theme.palette.mode === 'dark'
                                        ? 'rgba(255, 255, 255, 0.02)'
                                        : 'rgba(0, 0, 0, 0.015)',
                                    borderColor: 'divider',
                                    transition: 'all 0.2s',
                                    '&:hover': {
                                        borderColor: 'primary.main',
                                        color: 'primary.main',
                                        bgcolor: theme.palette.mode === 'dark'
                                            ? 'rgba(45, 107, 226, 0.1)'
                                            : 'rgba(45, 107, 226, 0.05)',
                                        transform: 'translateY(-2px)',
                                    },
                                }}
                            />
                        ))}
                    </Stack>
                </Box>
            </Card>
        </Container>
    );
}

export default NotFound;
